import { useMsal } from "@azure/msal-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import styled from "styled-components";

import { LogoWrapper, StyledDiv, TitleWrapper } from "src/app/pages/LoginPage/styles/LoginStyle";
import { TitleLogin } from "src/app/pages/LoginPage/styles/TitleLoginStyle";
import { Logo } from "../../../components";
import { ActionBar } from "../../../components/ActionBar/ActionBar";
import { PrimaryButton } from "../../../components/Buttons/components/ActionButton";
import { Logos, LogoSize } from "../../../components/Logo/Logo";
import { getAssignedDataGroup, setAccessToken } from "../../../core/router/auth";
import { setIdToken, setUserRights } from "../../../core/router/auth/SecurityManager";
import { routes } from "../../../core/router/config/routes";
import { getBeBaseUrl } from "../../../core/setup/config/utils";
import { Palette } from "../../../core/styles/palette";

const fetchUserRightsByDataGroup = async (baseUrl: string, idToken: string, dataGroupCode: string) => {
    const response = await fetch(`${baseUrl}switch-data-group?dataGroupCode=${encodeURIComponent(dataGroupCode)}`, {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${idToken}`,
        },
    });
    if (!response.ok) throw new Error("Failed to fetch user rights by data group");
    return response.json();
};

const fetchUserRightsByRoles = async (baseUrl: string, idToken: string, roles: string[]) => {
    const response = await fetch(`${baseUrl}user-rights`, {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${idToken}`,
        },
        body: JSON.stringify({ userRoles: roles }),
    });
    if (!response.ok) throw new Error("Failed to fetch user rights by roles");
    return response.json();
};

export const ApaMSELoginPage = () => {
    const { t } = useTranslation();
    const { instance } = useMsal();
    const navigate = useNavigate();
    const baseUrl = getBeBaseUrl();
    const [error, setError] = useState<string>();

    const handleMicrosoftLogin = async () => {
        setError(undefined);

        try {
            const loginResponse = await instance.loginPopup({
                scopes: ["User.Read openid profile offline_access"],
            });

            if (!loginResponse || !loginResponse.accessToken) {
                throw new Error("Login failed: No access token received");
            }

            instance.setActiveAccount(loginResponse.account);

            const { accessToken, idToken, idTokenClaims } = loginResponse;
            const claims = idTokenClaims as { roles?: string[] };
            let userRights: string[] = [];

            const dataGroup = getAssignedDataGroup();

            // IF there is already a DG assigned, get the rights assigned to the DG, not the user.
            if (dataGroup) {
                const data = await fetchUserRightsByDataGroup(baseUrl, idToken, dataGroup.code ?? "");
                userRights = data.userRights;
            } else if (claims.roles && claims.roles.length > 0) {
                const data = await fetchUserRightsByRoles(baseUrl, idToken, claims.roles);
                userRights = data.userRights;
            }

            setUserRights(userRights);
            setAccessToken(accessToken);
            setIdToken(idToken);

            navigate(routes.landingPage.path);
        } catch (err) {
            setError((err as Error)?.message || "Microsoft login failed");
        }
    };

    return (
        <StyledDiv data-name="login_form">
            <LogoWrapper>
                <Logo src={Logos.AeviLogoRebrand} size={LogoSize.REGULAR} />
            </LogoWrapper>
            <TitleWrapper>
                <TitleLogin>{`${t("page.title.login")}`}</TitleLogin>
            </TitleWrapper>
            <ActionBar>
                <PrimaryButton name="submit" htmlType="submit" onClick={handleMicrosoftLogin}>
                    {`${t("button.submit")}`}
                </PrimaryButton>
            </ActionBar>

            {error && (
                <LoginErrorContainer>
                    <LoginErrorText>{error}</LoginErrorText>
                </LoginErrorContainer>
            )}
        </StyledDiv>
    );
};

export default ApaMSELoginPage;

export const LoginErrorContainer = styled.div`
    margin-top: 16px;
    padding: 12px;
    background-color: ${Palette.PalePink};
    border: 1px solid ${Palette.LightPink};
    border-radius: 4px;
`;

export const LoginErrorText = styled.div`
    font-family: "RobotoRegular", sans-serif;
    color: ${Palette.AeviRed};
`;
